import {
  SegmentCell,
  SegmentMetric,
} from 'components/segment-grid/segment-grid.types';
import {
  GuestJourneySettings,
  GuestJourneyStageConfig,
  GuestJourneyStageGrid,
  GuestJourneyStageSnapshotEntry,
} from './guest-journey-reports.types';

/**
 * Fixed track maximum for both Settings sliders. The design grows the track
 * with the upper handle; we deliberately pin it so the visit-count and
 * average-check sliders share one bounded scale.
 */
export const GUEST_JOURNEY_STAGE_SLIDER_MAX = 200;

/** The four tunable stage boundaries — the numeric part of the settings DTO. */
export type GuestJourneyStageThresholds = Pick<
  GuestJourneySettings,
  | 'visitTier1Threshold'
  | 'visitTier2Threshold'
  | 'spendTier1Threshold'
  | 'spendTier2Threshold'
>;

/**
 * Recommended starting boundaries. The live values come from the client domain
 * (`getGuestJourneySettingsDefaultsQuery`); this constant is the mock/fixture
 * seed and the shape reference for that platform recommendation.
 */
export const GUEST_JOURNEY_DEFAULT_THRESHOLDS: GuestJourneyStageThresholds = {
  visitTier1Threshold: 10,
  visitTier2Threshold: 40,
  spendTier1Threshold: 19,
  spendTier2Threshold: 39,
};

/** True when all four boundaries already match the recommended `defaults`. */
export const isAtDefaultThresholds = (
  thresholds: GuestJourneyStageThresholds,
  defaults: GuestJourneyStageThresholds,
): boolean =>
  thresholds.visitTier1Threshold === defaults.visitTier1Threshold &&
  thresholds.visitTier2Threshold === defaults.visitTier2Threshold &&
  thresholds.spendTier1Threshold === defaults.spendTier1Threshold &&
  thresholds.spendTier2Threshold === defaults.spendTier2Threshold;

const METRIC_GRID: Record<SegmentMetric, keyof GuestJourneyStageGrid> = {
  count: 'countGrid',
  spend: 'spendGrid',
  visits: 'visitsGrid',
};

/**
 * Selects one metric's display-ready cells from the stage grid DTO and maps
 * each BE `PulseKpiCard` to the grid cell view-model. Cells stay in canonical
 * `SPEND_TIER_x_VISIT_TIER_y` order; the grid handles row/column placement.
 */
export const selectStageGridCells = (
  grid: GuestJourneyStageGrid | undefined,
  metric: SegmentMetric,
): SegmentCell[] =>
  (grid?.[METRIC_GRID[metric]]?.cells ?? []).map(card => ({
    title: card.title,
    value: card.value,
    movement: card.trend,
  }));

/**
 * The fixed Guest Journey stage taxonomy — the BE `stage` enum values, used
 * verbatim as the FE filter option ids / URL search-param values (one
 * identifier end-to-end, aligned to the backend). The 9 spend×visit segments
 * render settings-driven labels via `getStageLabel`. (`SLIDER`/`CHURNED` were
 * dropped from the Stage Movement `stage` enum in API v0.4 — they are no longer
 * valid filter values.) Order is the Stage Movement dropdown order (per
 * product): lowest spend tier first with all three visit tiers, then the next
 * spend tier — i.e. spend tier1→tier3, and within each spend tier visit
 * tier1→tier3. Only consumed by `toStageOptions`; the stage grid derives its own
 * cell order (see `selectStageGridCells`).
 */
export const GUEST_JOURNEY_STAGES = [
  'SPEND_TIER_1_VISIT_TIER_1',
  'SPEND_TIER_1_VISIT_TIER_2',
  'SPEND_TIER_1_VISIT_TIER_3',
  'SPEND_TIER_2_VISIT_TIER_1',
  'SPEND_TIER_2_VISIT_TIER_2',
  'SPEND_TIER_2_VISIT_TIER_3',
  'SPEND_TIER_3_VISIT_TIER_1',
  'SPEND_TIER_3_VISIT_TIER_2',
  'SPEND_TIER_3_VISIT_TIER_3',
] as const;

/**
 * Maps a BE stage enum (`SPEND_TIER_2_VISIT_TIER_2`) to the client's configured
 * label ("{spend tier2} {visit tier2}"). Anything unrecognized passes through
 * unchanged — as do all stages while settings load.
 */
export const getStageLabel = (
  stage: string,
  settings?: GuestJourneySettings,
): string => {
  if (!settings) return stage;
  const match = /^SPEND_TIER_([123])_VISIT_TIER_([123])$/.exec(stage);
  if (!match) return stage;
  const spendLabel = settings[
    `spendTier${match[1]}Label` as keyof GuestJourneySettings
  ] as string;
  const visitLabel = settings[
    `visitTier${match[2]}Label` as keyof GuestJourneySettings
  ] as string;
  return `${spendLabel} ${visitLabel}`;
};

/**
 * The GJ V2 customer-tag keys whose stored values are numeric tiers (1/2/3) and
 * therefore need the client's configured stage name shown in place of the number.
 * (`GJ_V2_ATTRITION_RISK` is intentionally excluded — its values NO_RISK/SLIDER/
 * CHURNED are already words and are not client-configurable.)
 */
export const GUEST_JOURNEY_SPEND_STAGE_TAG = 'GJ_V2_SPEND_STAGE';
export const GUEST_JOURNEY_VISIT_STAGE_TAG = 'GJ_V2_VISIT_STAGE';

/** True when the tag key is one of the numeric GJ V2 stage tags. */
export const isGuestJourneyStageTag = (tagKey: string): boolean =>
  tagKey === GUEST_JOURNEY_SPEND_STAGE_TAG ||
  tagKey === GUEST_JOURNEY_VISIT_STAGE_TAG;

/**
 * Value→label map for a GJ V2 stage customer tag: the numeric tier (1/2/3) mapped
 * to the client's configured spend/visit tier name (the same labels the GJ report
 * renders via {@link getStageLabel}). Returns undefined for any non-stage tag, or
 * before settings load, so callers fall back to the raw value.
 */
export const getGuestJourneyStageTagValueLabels = (
  tagKey: string,
  settings?: GuestJourneySettings,
): Record<string, string> | undefined => {
  if (!settings) return undefined;
  if (tagKey === GUEST_JOURNEY_SPEND_STAGE_TAG) {
    return {
      '1': settings.spendTier1Label,
      '2': settings.spendTier2Label,
      '3': settings.spendTier3Label,
    };
  }
  if (tagKey === GUEST_JOURNEY_VISIT_STAGE_TAG) {
    return {
      '1': settings.visitTier1Label,
      '2': settings.visitTier2Label,
      '3': settings.visitTier3Label,
    };
  }
  return undefined;
};

/**
 * Stage filter options for Stage Movement: the 9 (spend × visit) combos with
 * settings-driven labels.
 */
export const toStageOptions = (
  settings?: GuestJourneySettings,
): { id: string; label: string }[] =>
  GUEST_JOURNEY_STAGES.map(stage => ({
    id: stage,
    label: getStageLabel(stage, settings),
  }));

/**
 * Derives the display-ordered tier view-model from the settings DTO: labels +
 * explicit min/max per tier. Spend rows render highest tier first; visit
 * columns lowest first. Tier1 lower bounds are implicit ($0 spend, 1 visit);
 * tier3 is open-ended.
 */
/**
 * Money boundaries are inclusive, so the next spend tier starts one cent above
 * where the previous ends (e.g. Value ends $19.00 → Core starts $19.01). Rounded
 * to cents to avoid float artifacts. (Visit tiers use whole-visit `+1`.)
 */
export const centAbove = (value: number): number =>
  Math.round((value + 0.01) * 100) / 100;

export const toStageConfig = (
  settings: GuestJourneySettings,
): GuestJourneyStageConfig => ({
  spendTiers: [
    {
      name: settings.spendTier3Label,
      thresholdMin: centAbove(settings.spendTier2Threshold),
    },
    {
      name: settings.spendTier2Label,
      thresholdMin: centAbove(settings.spendTier1Threshold),
      thresholdMax: settings.spendTier2Threshold,
    },
    {
      name: settings.spendTier1Label,
      thresholdMin: 0,
      thresholdMax: settings.spendTier1Threshold,
    },
  ],
  visitTiers: [
    {
      name: settings.visitTier1Label,
      thresholdMin: 1,
      thresholdMax: settings.visitTier1Threshold,
    },
    {
      name: settings.visitTier2Label,
      thresholdMin: settings.visitTier1Threshold + 1,
      thresholdMax: settings.visitTier2Threshold,
    },
    {
      name: settings.visitTier3Label,
      thresholdMin: settings.visitTier2Threshold + 1,
    },
  ],
});

/**
 * True when a stage row's name or range differs between two snapshots. Drives
 * the per-row change highlight in the "Threshold Changes" drawer; the snapshot
 * rows are display-ready (`GuestJourneyStageSnapshotEntry`), supplied by the BE.
 */
export const stageRowChanged = (
  a: GuestJourneyStageSnapshotEntry,
  b: GuestJourneyStageSnapshotEntry,
): boolean => a.name !== b.name || a.range !== b.range;
